import React, { useState, useEffect } from 'react';
import { breakingAPI, pushAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Zap, Plus, Pencil, Trash2, X, Bell, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';

const PRIORITY_COLORS = {
  low: 'bg-gray-100 text-gray-700',
  medium: 'bg-blue-50 text-blue-700',
  high: 'bg-amber-50 text-amber-700',
  critical: 'bg-red-50 text-red-700',
};

export default function AdminBreakingNews() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ headline: '', content: '', priority: 'high', is_active: true });
  const [showPush, setShowPush] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [pushItem, setPushItem] = useState(null);

  useEffect(() => { loadItems(); }, []);

  const loadItems = async () => {
    try {
      const res = await breakingAPI.list(false);
      setItems(res.data.breaking_news || []);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.headline.trim()) { toast.error('Headline required'); return; }
    try {
      if (editId) {
        await breakingAPI.update(editId, form);
        toast.success('Breaking news updated!');
      } else {
        await breakingAPI.create(form);
        toast.success('Breaking news created!');
      }
      setShowForm(false);
      setEditId(null);
      setForm({ headline: '', content: '', priority: 'high', is_active: true });
      loadItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleEdit = (item) => {
    setForm({ headline: item.headline, content: item.content || '', priority: item.priority, is_active: item.is_active });
    setEditId(item.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this breaking news?')) return;
    try {
      await breakingAPI.delete(id);
      toast.success('Deleted');
      loadItems();
    } catch { toast.error('Failed to delete'); }
  };

  const toggleActive = async (item) => {
    try {
      await breakingAPI.update(item.id, { is_active: !item.is_active });
      toast.success(item.is_active ? 'Deactivated' : 'Activated');
      loadItems();
    } catch { toast.error('Failed'); }
  };

  const handleSendPush = async () => {
    if (!pushItem) return;
    setPushLoading(true);
    try {
      await pushAPI.send({
        title: 'Breaking News',
        body: pushItem.headline,
        data: { type: 'breaking', id: pushItem.id },
        topic: 'breaking_news',
      });
      toast.success('Push notification sent to all subscribers!');
      setShowPush(false);
      setPushItem(null);
    } catch { toast.error('Failed to send push'); }
    finally { setPushLoading(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Breaking News</h1>
          <p className="text-sm text-[#5B616E] mt-1">Manage breaking news ticker</p>
        </div>
        <Button className="bg-[#B3261E] text-white hover:bg-[#9E1F19]" onClick={() => { setEditId(null); setForm({ headline: '', content: '', priority: 'high', is_active: true }); setShowForm(true); }}>
          <Plus className="w-4 h-4 mr-2" /> Add Breaking News
        </Button>
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editId ? 'Edit' : 'New'} Breaking News</DialogTitle>
            <DialogDescription>This will appear on the ticker bar across the site.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium block mb-1">Headline</label>
              <input
                value={form.headline}
                onChange={e => setForm({ ...form, headline: e.target.value })}
                placeholder="Breaking: Major event happening now..."
                className="w-full border border-[#E7E0D6] rounded-lg px-3 py-2 text-sm bg-white"
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Details (optional)</label>
              <textarea
                value={form.content}
                onChange={e => setForm({ ...form, content: e.target.value })}
                placeholder="Additional context..."
                className="w-full border border-[#E7E0D6] rounded-lg px-3 py-2 text-sm bg-white resize-none"
                rows={3}
              />
            </div>
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="text-sm font-medium block mb-1">Priority</label>
                <select
                  value={form.priority}
                  onChange={e => setForm({ ...form, priority: e.target.value })}
                  className="w-full border border-[#E7E0D6] rounded-lg px-3 py-2 text-sm bg-white"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
              <div className="flex-1">
                <label className="text-sm font-medium block mb-1">Status</label>
                <label className="flex items-center gap-2 mt-2 cursor-pointer text-sm">
                  <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} className="rounded" />
                  Active
                </label>
              </div>
            </div>
            <Button type="submit" className="w-full bg-[#B3261E] text-white hover:bg-[#9E1F19]">
              {editId ? 'Update' : 'Publish'} Breaking News
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Push Notification Dialog */}
      <Dialog open={showPush} onOpenChange={setShowPush}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Push Notification</DialogTitle>
            <DialogDescription>Send this breaking news as a push notification to all subscribed users.</DialogDescription>
          </DialogHeader>
          {pushItem && (
            <div className="bg-[#FBF7EF] rounded-lg p-4 border border-[#E7E0D6]">
              <p className="font-medium text-sm">{pushItem.headline}</p>
              {pushItem.content && <p className="text-xs text-[#5B616E] mt-1">{pushItem.content}</p>}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowPush(false)}>Cancel</Button>
            <Button className="bg-[#B3261E] text-white hover:bg-[#9E1F19]" onClick={handleSendPush} disabled={pushLoading}>
              {pushLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Bell className="w-4 h-4 mr-1" />} Send Push
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {loading ? (
        <div className="space-y-4">{[1,2,3].map(i => <Skeleton key={i} className="h-20 rounded-xl" />)}</div>
      ) : items.length === 0 ? (
        <Card className="bg-white border-[#E7E0D6] p-12 text-center">
          <Zap className="w-12 h-12 mx-auto mb-4 text-[#E7E0D6]" />
          <p className="text-[#5B616E]">No breaking news. Create one to display on the ticker.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map(item => (
            <Card key={item.id} className={`bg-white border-[#E7E0D6] ${!item.is_active ? 'opacity-60' : ''}`}>
              <CardContent className="py-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Zap className="w-4 h-4 text-[#B3261E]" />
                      <h3 className="font-medium text-sm">{item.headline}</h3>
                    </div>
                    {item.content && <p className="text-xs text-[#5B616E] ml-6">{item.content}</p>}
                    <div className="flex items-center gap-3 ml-6 mt-2">
                      <Badge className={`text-[10px] ${PRIORITY_COLORS[item.priority] || ''}`}>{item.priority}</Badge>
                      <Badge variant={item.is_active ? 'default' : 'secondary'} className="text-[10px]">{item.is_active ? 'Active' : 'Inactive'}</Badge>
                      <span className="text-xs text-[#5B616E]">{new Date(item.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" className="text-[#1E5A8A]" title="Send Push Notification" onClick={() => { setPushItem(item); setShowPush(true); }}>
                      <Bell className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => toggleActive(item)}>
                      {item.is_active ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleEdit(item)}><Pencil className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(item.id)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
